/**
 * PDF page selection helpers for Step 5 crop/upload flow.
 */

/**
 * Parse a page range string like "1-3, 5, 8-10" into sorted unique 1-based page numbers.
 * @param {string} input
 * @param {number} totalPages
 * @returns {{ pages: number[], error: string | null }}
 */
export function parsePdfPageRange(input, totalPages) {
  const raw = String(input || '').trim();
  if (!raw) {
    return { pages: [], error: 'Enter a page range (example: 1-3, 5, 8-10).' };
  }

  const pages = new Set();
  const parts = raw.split(',').map((p) => p.trim()).filter(Boolean);

  for (const part of parts) {
    if (/^\d+\s*-\s*\d+$/.test(part)) {
      const [startStr, endStr] = part.split('-').map((s) => s.trim());
      const start = parseInt(startStr, 10);
      const end = parseInt(endStr, 10);
      if (Number.isNaN(start) || Number.isNaN(end)) {
        return { pages: [], error: `Invalid range: ${part}` };
      }
      if (start < 1 || end < 1 || start > totalPages || end > totalPages) {
        return { pages: [], error: `Range ${part} is outside pages 1-${totalPages}.` };
      }
      const lo = Math.min(start, end);
      const hi = Math.max(start, end);
      for (let p = lo; p <= hi; p += 1) pages.add(p);
      continue;
    }

    if (/^\d+$/.test(part)) {
      const page = parseInt(part, 10);
      if (page < 1 || page > totalPages) {
        return { pages: [], error: `Page ${page} is outside pages 1-${totalPages}.` };
      }
      pages.add(page);
      continue;
    }

    return { pages: [], error: `Invalid page token: ${part}` };
  }

  if (pages.size === 0) {
    return { pages: [], error: 'No valid pages found in range.' };
  }

  return { pages: Array.from(pages).sort((a, b) => a - b), error: null };
}

export function allPdfPages(totalPages) {
  return Array.from({ length: Math.max(0, totalPages) }, (_, i) => i + 1);
}

/** Crop box smaller than threshold (percent) may remove required proof details. */
export function isCropAreaUnusuallySmall(cropArea, thresholdPercent = 35) {
  if (!cropArea) return false;
  return cropArea.width < thresholdPercent || cropArea.height < thresholdPercent;
}

export function formatPageList(pages) {
  if (!pages?.length) return '';
  return pages.join(', ');
}
